import React, { ReactElement, useContext, useEffect, useState } from 'react';
import Buttons from '@components/Buttons';
import Button from '@components/DesignLibrary/Button';
import useNoodleApi from '@hooks/useNoodleApi';
import { deserializeHtml, slateHasText } from '@components/SlateEditor';
import { Descendant } from 'slate';
import createRichTextAst from '@helpers/createRichTextAst';
import ProgressIndicator from '@components/ProgressIndicator';
import InputComposer from '@components/InputComposer';
import updateWorkflowStep from '@tsClient/workflows/updateWorkflowStep';
import { AssignToData, MESSAGE_ATTACHMENT_REFERENCE_TYPES, SlateNode } from '@typings/api-models';
import { CaretRight, PlusCircle } from '@phosphor-icons/react';
import { useToast } from '@/hooks';
import { Textbox } from '@phosphor-icons/react/dist/ssr';
import TrashSimple from '@components/Icons/TrashSimple';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import TeamsContext from '@/providers/Teams/TeamsContext';

import s from './EditSendMessageActionForm.module.scss';
import { Switch } from '../DesignLibrary/Atoms';
import TextInput from '../FormFields/TextInput';
import CollapsibleArea from '../CollapsibleArea';
import Spacer from '../Spacer';
import Modal from '../Modal';

export type SendMessageActionData = {
  message: {
    attachments: Array<{
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
      title: string;
      id: string;
    } & { assignTo?: AssignToData[] }>;
    slate: SlateNode[],
    html: string,
    text: string,
    medias: { id: string; name: string; }[],
    handbooks: { id: string; title?: string | null; }[],
    emailBody: {
      medias: { id: string; name: string; }[],
      slate: SlateNode[],
      html: string,
      text: string,
    } | null;
    emailSubject: string | null;
    sendEmailUponCompletion: boolean;
    cc: string[];
    bccAssignee: boolean;
  },
};

type Props = {
  actionId: string;
  messageData?: SendMessageActionData | null;
  workflowId: string;
  onSave: () => void;
  currentPage?: 'edit-step' | 'send-email';
  setCurrentPage?: (page: 'edit-step' | 'send-email') => void;
};

type Values = {
  attachments: { referenceId: string; referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES; title: string; id: string; }[];
  slate: { children: Descendant[] }[] | null;
  media?: { id: string; name: string; }[];
  files?: (File | string)[];
  handbooks?: { id: string; title?: string | null; }[];
  emailBody?: {
    slate: { children: Descendant[] }[] | null;
    medias?: { id: string; name: string; }[];
  };
  emailSubject?: string;
  sendEmailUponCompletion?: boolean;
  cc: string[];
  bccAssignee: boolean;
};

const EditSendMessageActionForm = ({
  actionId,
  messageData,
  workflowId,
  onSave,
  currentPage = 'edit-step',
  setCurrentPage,
}: Props): ReactElement => {
  const addToast = useToast();
  const { currentTeamOwner } = useContext(TeamsContext);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [values, setValues] = useState<Values>({
    attachments: messageData?.message?.attachments || [],
    bccAssignee: messageData?.message?.bccAssignee || false,
    cc: messageData?.message?.cc || [],
    emailBody: {
      medias: messageData?.message?.emailBody?.medias || [],
      slate: messageData?.message?.emailBody ? null : [createRichTextAst('')],
    },
    emailSubject: messageData?.message?.emailSubject || '',
    files: [],
    handbooks: messageData?.message?.handbooks || [],
    media: messageData?.message?.medias || [],
    sendEmailUponCompletion: messageData?.message?.sendEmailUponCompletion || false,
    slate: messageData?.message ? null : [createRichTextAst('')],
  });

  const defaultInputs = ['{{customerName}}'];

  const [isAddCcModalOpen, setIsAddCcModalOpen] = useState(false);
  const [newCcEmail, setNewCcEmail] = useState('');
  const [newCcError, setNewCcError] = useState('');

  const canShowBankIntegrationFeature = useFeatureFlag('bank-statements-organizer', { creatorId: currentTeamOwner?.slug });

  async function copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      addToast(useToast.ToastTypeVariants.SUCCESS, `${text} copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }

  const handleSlateChange = (
    slate: { children: Descendant[] }[],
    isEmailBody: boolean = false,
  ): void => {
    if (isEmailBody) {
      setValues(val => ({
        ...val,
        emailBody: {
          ...val.emailBody,
          slate,
        },
      }));
    } else {
      setValues(val => ({
        ...val,
        emailBody: {
          ...val.emailBody,
          slate: val.emailBody?.slate || null,
        },
        slate,
      }));
    }
  };
  const handleMediaChange = (
    media: { id: string; name?: string | null; uploadPercentage?: number }[],
    isEmailBody: boolean = false,
  ): void => {
    if (isEmailBody) {
      setValues(val => ({
        ...val,
        emailBody: {
          ...val.emailBody,
          medias: media.map((m) => ({
            ...m,
            name: m.name || 'media',
          })),
          slate: val.emailBody?.slate || null,
        },
      }));
    } else {
      setValues(val => ({
        ...val,
        emailBody: {
          ...val.emailBody,
          slate: val.emailBody?.slate || null,
        },
        media: media.map((m) => ({
          ...m,
          name: m.name || 'media',
        })),
      }));
    }
  };
  const handleHandbookChange = (
    handbooks: { id: string; title?: string | null; }[],
  ): void => {
    setValues(val => ({ ...val, handbooks }));
  };
  const handleAttachmentChange = (
    attachments: {
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
      title: string;
      id: string;
    }[],
  ): void => {
    setValues(val => ({
      ...val,
      attachments,
      emailBody: {
        ...val.emailBody,
        slate: val.emailBody?.slate || null,
      },
    }));
  };

  const { getData: updateWorkflowStepFn } = useNoodleApi(updateWorkflowStep, { toastOnError: true });

  useEffect(() => {
    if (messageData?.message && messageData?.message.html) {
      const document = new DOMParser().parseFromString(messageData?.message.html, 'text/html');
      setValues(oldValues => ({
        ...oldValues,
        slate: deserializeHtml(document.body) as { children: Descendant[] }[],
      }));
    }
    if (messageData?.message && messageData?.message.emailBody?.html) {
      const document = new DOMParser().parseFromString(messageData?.message.emailBody.html, 'text/html');
      setValues(oldValues => ({
        ...oldValues,
        emailBody: {
          ...oldValues.emailBody,
          slate: deserializeHtml(document.body) as { children: Descendant[] }[],
        },
      }));
    }
    setIsInitialized(true);
  }, [messageData?.message]);

  if (!isInitialized) {
    return <ProgressIndicator isCentered/>;
  }

  const emailHasContent = (): boolean => {
    const slate = values.emailBody?.slate;
    const subject = values.emailSubject;

    const hasEmailSlate = slateHasText(slate || []);
    const hasEmailSubject = subject;

    if (!hasEmailSlate && !hasEmailSubject) {
      addToast(useToast.ToastTypeVariants.ERROR, 'Please add Subject line and body to Email Template');
      return false;
    }
    if (!hasEmailSlate) {
      addToast(useToast.ToastTypeVariants.ERROR, 'Please add body to Email Template');
      return false;
    }
    if (!hasEmailSubject) {
      addToast(useToast.ToastTypeVariants.ERROR, 'Please add Subject line to Email Template');
      return false;
    }
    return true;
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    if (values) {
      if (values.sendEmailUponCompletion) {
        if(!emailHasContent()) {
          setCurrentPage?.('send-email');
          setIsSaving(false);
          return;
        }
      }
      const sendMessageData = {
        attachments: values.attachments,
        bccAssignee: values.bccAssignee,
        cc: values.cc,
        emailBody: values.emailBody,
        emailSubject: values.emailSubject,
        handbooks: values.handbooks,
        medias: values.media,
        sendEmailUponCompletion: values.sendEmailUponCompletion,
        slate: values.slate,
      };

      await updateWorkflowStepFn({
        id: workflowId,
        stepId: actionId,
        updates: {
          data: { message: sendMessageData },
        },
      });
      onSave();
    }
  };

  return (
    <>
      <div className={s.craftWelcomeMessageForm}>
        {currentPage === 'edit-step' && (
          <>
            <div>
              <InputComposer
                isRichText
                rows={3}
                showAddBooking
                showAddVideo
                showAddAnyFile
                showAddDocumentRequest
                showAddCreditReport
                showAddFormRequest
                showAddPaymentLink
                showAddInvoice
                showAddCustomTerms
                showBankAccountIntegration={canShowBankIntegrationFeature}
                showSendButton={false}
                onSend={handleSave}
                onChangeMedia={handleMediaChange}
                onChangeHandbooks={handleHandbookChange}
                onChangeText={handleSlateChange}
                onChangeAttachments={handleAttachmentChange}
                showProTips={false}
                initialText={values.slate}
                initialAttachments={values.attachments}
                initialMedia={values.media}
                initialHandbooks={values.handbooks}
                isFetching={false}
                placeholder="Type your message"
                label="Message Body"
                showAddHandbook
                referenceId={'workflowId'}
                referenceType={'action-message'}
              />
            </div>
            <div className={s.craftWelcomeMessageFormSendEmail} onClick={() => setCurrentPage?.('send-email')}>
              <div onClick={e => e.stopPropagation()}>
                <Switch
                  name="sendEmail"
                  label="Send email upon completion"
                  isChecked={values.sendEmailUponCompletion || false}
                  onChange={isChecked => {
                    setValues(val => ({ ...val, sendEmailUponCompletion: isChecked }));
                  }}
                />
              </div>
              <CaretRight />
            </div>
            <Buttons isFetching={isSaving} isFullWidth isSecondary onClick={handleSave}>
              Update Step
            </Buttons>
          </>
        )}
        {currentPage === 'send-email' && (
          <>
            <div>
              <TextInput
                id={'emailSubject'}
                value={values.emailSubject || ''}
                onChange={e => setValues(val => ({ ...val, emailSubject: e }))}
                placeholder={`Email Subject`}
                label="Email Subject"
                required
              />
              <Spacer size={6} />
              <div className={s.inputs__variables}>
                <div className={s.ccChipsContainer}>
                  {values.cc.map((email) => (
                    <span key={email} className={s.terms__variables__chip}>
                      {email}
                      <button
                        className={s.terms__variables__chipDelete}
                        onClick={() => setValues(val => ({ ...val, cc: val.cc.filter(e => e !== email) }))}
                        aria-label={`Remove ${email}`}
                        type="button"
                      >
                        <TrashSimple weight="fill" size={16} color="var(--color-error)" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className={s.ccInputRow}>
                  <Button size="sm" variant="secondary" onClick={() => setIsAddCcModalOpen(true)} className={s.terms__variables__add}>
                    <PlusCircle size={16} weight="fill" />
                      Add CC Email
                  </Button>
                </div>
              </div>
              <Spacer size={6} />
              <CollapsibleArea title="Inputs" Icon={{ Component: Textbox }}>
                <div className={s.inputs__padding}>
                  <div className={s.inputs__variables}>
                    <p className="body-sm-bold">Available Inputs</p>
                    <Spacer size={6} />
                    <p className="caption">
                      Use these in your email template to call dynamic variables that generate unique versions for each individual
                      customer based on their information.
                    </p>
                    <Spacer size={12} />
                    {defaultInputs.length > 0 && (
                      <ul>
                        {defaultInputs.map(input => (
                          <li key={input}>
                            <button onClick={() => copyToClipboard(input)}>{input}</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </CollapsibleArea>
              <Spacer size={12} />
              <InputComposer
                isRichText
                rows={3}
                showAddAnyFile
                customAccept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                showAddAudio={false}
                showSendButton={false}
                onChangeText={e => handleSlateChange(e, true)}
                onChangeMedia={e => handleMediaChange(e, true)}
                showProTips={false}
                initialText={values.emailBody?.slate}
                initialMedia={values.emailBody?.medias}
                isFetching={false}
                placeholder="Email Body"
                label="Email Body"
                referenceId={'workflowId'}
                referenceType={'action-message'}
              />
            </div>
            <div className={s.inputs__variables}>
              <input
                type="checkbox"
                id="bccAssignee"
                checked={values.bccAssignee}
                onChange={(e) => setValues(val => ({ ...val, bccAssignee: e.target.checked }))}
              />
              <label htmlFor="bccAssignee">BCC a copy of the email to the assignee</label>
            </div>
            <div>
              <Buttons
                isFullWidth
                isSecondary
                onClick={() => emailHasContent() && setCurrentPage?.('edit-step')}
              >
                Save Email
              </Buttons>
            </div>
          </>
        )}
      </div>
      {isAddCcModalOpen && (
        <Modal onClose={() => setIsAddCcModalOpen(false)} hasPadding title="Add CC Email">
          <TextInput
            id="new-cc-email"
            value={newCcEmail}
            onChange={setNewCcEmail}
            placeholder="Enter email address"
            label="CC Email"
            error={newCcError}
          />
          <Spacer size={12} />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newCcEmail)) {
                setNewCcError('Invalid email address');
                return;
              }
              if (values.cc.includes(newCcEmail)) {
                setNewCcError('Email already added');
                return;
              }
              setValues(val => ({ ...val, cc: [...val.cc, newCcEmail] }));
              setNewCcEmail('');
              setNewCcError('');
              setIsAddCcModalOpen(false);
            }}
          >
            Add
          </Button>
        </Modal>
      )}
    </>
  );
};

export default EditSendMessageActionForm;
